"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Zap, Shield } from "lucide-react";
import type { EfficiencyMetrics as EfficiencyMetricsType, TimeSavedBreakdown } from "../types";

interface EfficiencyMetricsProps {
  efficiency: EfficiencyMetricsType;
  breakdown: TimeSavedBreakdown;
}

function formatMinutes(mins: number): string {
  if (mins < 60) return `${Math.round(mins)} min`;
  const hours = mins / 60;
  if (hours < 24) return `${hours.toFixed(1)} hrs`;
  const days = hours / 8; // working days
  return `${days.toFixed(1)} days`;
}

interface BreakdownLine {
  label: string;
  count: number;
  minutes: number;
  isNegative?: boolean;
}

export function EfficiencyMetrics({ efficiency, breakdown }: EfficiencyMetricsProps) {
  const lines: BreakdownLine[] = [
    { label: "Auto-sent", count: breakdown.autoSent.count, minutes: breakdown.autoSent.minutesSaved },
    { label: "Drafts", count: breakdown.drafts.count, minutes: breakdown.drafts.minutesSaved },
    { label: "Skipped", count: breakdown.skipped.count, minutes: breakdown.skipped.minutesSaved },
    { label: "Escalations", count: breakdown.handovers.count, minutes: breakdown.handovers.minutesSaved },
    { label: "Errors", count: breakdown.errors.count, minutes: breakdown.errors.minutesLost, isNegative: true },
  ];

  const workingDays = efficiency.timeSavedMin / 60 / 8;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Left: Time Saved breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Time Saved</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-foreground mb-1">
            {formatMinutes(efficiency.timeSavedMin)}
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            <span>~</span>{workingDays.toFixed(1)} <span>working days &middot;</span> {efficiency.equivalentFte.toFixed(2)} <span>FTE equivalent</span>
          </p>

          <div className="space-y-2">
            {lines.map((line) => (
              <div key={line.label} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {line.label}
                  <span className="text-xs ml-1">({line.count.toLocaleString()})</span>
                </span>
                <span className={`font-medium tabular-nums ${line.isNegative ? "text-red-600" : "text-green-600"}`}>
                  {line.isNegative ? "-" : "+"}
                  {formatMinutes(line.minutes)}
                </span>
              </div>
            ))}
            <div className="border-t pt-2 flex items-center justify-between text-sm font-medium">
              <span>Total</span>
              <span className="text-green-600 tabular-nums">
                {formatMinutes(breakdown.totalMinutesSaved)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Right: Quick stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-50">
              <Zap className="w-7 h-7 text-green-600" />
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600">
                {(efficiency.automationRate * 100).toFixed(0)}<span>%</span>
              </div>
              <div className="text-sm font-medium">Automation Rate</div>
              <div className="text-xs text-muted-foreground">
                Emails resolved without human
              </div>
              <div className="text-[10px] text-muted-foreground/70">
                (auto-sent + skipped) / inbox emails
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-50">
              <Shield className="w-7 h-7 text-red-600" />
            </div>
            <div>
              <div className="text-3xl font-bold text-red-600">
                {(efficiency.blacklistRate * 100).toFixed(1)}<span>%</span>
              </div>
              <div className="text-sm font-medium">Blacklist Rate</div>
              <div className="text-xs text-muted-foreground">
                Senders in period currently blacklisted
              </div>
              <div className="text-[10px] text-muted-foreground/70">
                blacklisted senders / distinct senders
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
